import { useEffect, useRef, useState, type FormEvent } from "react";
import { Image as ImageIcon, Plus, Trash2, Upload, X } from "lucide-react";
import type { Product, ProductVariant } from "@am/shared";
import { useApiClient } from "../api/client";
import { useI18n } from "../i18n/I18nProvider";
import { toast } from "sonner";

interface ProductModalProps {
  product: Product | null;   // null = création
  currency: string;          // affichée à côté du prix
  onClose: () => void;
  onSaved: () => Promise<void>;
}

/**
 * Formulaire de création/édition d'un produit manuel.
 *
 * Champs :
 *   - Nom (obligatoire, max 200)
 *   - Description (textarea plain, max 4000)
 *   - Prix (number, ≥ 0)
 *   - URL image + aperçu miniature
 *   - URL produit (page tenant)
 *   - Variantes : sections dynamiques (label + options)
 *   - Actif/Inactif (toggle)
 */
export function ProductModal({ product, currency, onClose, onSaved }: ProductModalProps) {
  const api = useApiClient();
  const { t } = useI18n();

  const [name, setName] = useState(product?.name ?? "");
  const [description, setDescription] = useState(product?.description ?? "");
  const [price, setPrice] = useState<string>(
    product ? String(product.price) : "0",
  );
  const [imageUrl, setImageUrl] = useState(product?.image_url ?? "");
  const [productUrl, setProductUrl] = useState(product?.product_url ?? "");
  const [variants, setVariants] = useState<ProductVariant[]>(
    product?.variants?.length ? product.variants : [],
  );
  const [isActive, setIsActive] = useState(product?.is_active ?? true);
  const [imgError, setImgError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Escape → close
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Reset image error when URL changes
  useEffect(() => {
    setImgError(false);
  }, [imageUrl]);

  // --- Variantes helpers ---

  function addVariant() {
    setVariants((v) => [...v, { label: "", options: [""] }]);
  }

  function removeVariant(index: number) {
    setVariants((v) => v.filter((_, i) => i !== index));
  }

  function updateVariantLabel(index: number, label: string) {
    setVariants((v) => v.map((x, i) => (i === index ? { ...x, label } : x)));
  }

  function addVariantOption(vIndex: number) {
    setVariants((v) =>
      v.map((x, i) => (i === vIndex ? { ...x, options: [...x.options, ""] } : x)),
    );
  }

  function updateVariantOption(vIndex: number, oIndex: number, value: string) {
    setVariants((v) =>
      v.map((x, i) =>
        i === vIndex
          ? { ...x, options: x.options.map((o, j) => (j === oIndex ? value : o)) }
          : x,
      ),
    );
  }

  function removeVariantOption(vIndex: number, oIndex: number) {
    setVariants((v) =>
      v.map((x, i) =>
        i === vIndex
          ? { ...x, options: x.options.filter((_, j) => j !== oIndex) }
          : x,
      ),
    );
  }

  // --- Upload image ---

  async function onFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset la valeur pour permettre de re-sélectionner le même fichier ensuite.
    e.target.value = "";
    if (!file) return;

    setError(null);
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api<{ image_url: string }>(
        "/tenant/api/products/upload-image",
        { method: "POST", body: form },
      );
      setImageUrl(res.image_url);
      toast.success(t("products_image_upload_ok"));
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("error_generic");
      setError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  }

  // --- Submit ---

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError(t("products_error_name_required"));
      return;
    }

    const priceNum = parseFloat(price);
    if (isNaN(priceNum) || priceNum < 0) {
      setError(t("products_error_price_invalid"));
      return;
    }

    // Nettoie les variantes : élimine label vide ou options toutes vides
    const cleanVariants = variants
      .map((v) => ({
        label: v.label.trim(),
        options: v.options.map((o) => o.trim()).filter(Boolean),
      }))
      .filter((v) => v.label && v.options.length > 0);

    setSaving(true);
    try {
      const body = {
        name: trimmedName,
        description: description.trim(),
        price: priceNum,
        image_url: imageUrl.trim(),
        product_url: productUrl.trim(),
        variants: cleanVariants,
        is_active: isActive,
      };
      if (product) {
        await api(`/tenant/api/products/${product.id}`, { method: "PUT", body });
      } else {
        await api("/tenant/api/products", { method: "POST", body });
      }
      toast.success(t("products_save_ok"));
      await onSaved();
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("error_generic");
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="kb-modal-overlay is-open"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="kb-modal">
        <div className="kb-modal-header">
          <div className="kb-modal-title">
            {product ? t("products_modal_edit") : t("products_modal_new")}
          </div>
          <button type="button" className="kb-modal-close" onClick={onClose} aria-label={t("cancel")}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={onSubmit}>
          {/* Nom */}
          <div className="field" style={{ marginBottom: 12 }}>
            <label>{t("products_label_name")}</label>
            <input
              type="text"
              maxLength={200}
              placeholder={t("products_placeholder_name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>

          {/* Prix + image URL sur la même ligne (grid) */}
          <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 12, marginBottom: 12 }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>{t("products_label_price")} ({currency})</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>{t("products_label_image_url")}</label>
              <input
                type="url"
                placeholder="https://..."
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
              />
            </div>
          </div>

          {/* Upload image → MinIO (le lien externe ci-dessus reste possible) */}
          <div style={{ marginBottom: 12 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={onFileSelected}
              style={{ display: "none" }}
            />
            <button
              type="button"
              className="btn-sm btn-ghost"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <Upload size={14} /> {uploading ? t("products_image_uploading") : t("products_image_upload")}
            </button>
            <span style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: 8 }}>
              {t("products_image_upload_hint")}
            </span>
          </div>

          {/* Preview image */}
          {imageUrl.trim() && (
            <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 8,
                  border: "1px solid var(--border)",
                  background: "var(--bg-elevated)",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {imgError ? (
                  <ImageIcon size={20} color="var(--text-muted)" />
                ) : (
                  <img
                    src={imageUrl}
                    alt=""
                    onError={() => setImgError(true)}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                )}
              </div>
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                {imgError ? t("products_image_error") : t("products_image_preview")}
              </div>
            </div>
          )}

          {/* Product URL */}
          <div className="field" style={{ marginBottom: 12 }}>
            <label>{t("products_label_product_url")}</label>
            <input
              type="url"
              placeholder="https://..."
              value={productUrl}
              onChange={(e) => setProductUrl(e.target.value)}
            />
          </div>

          {/* Description */}
          <div className="field" style={{ marginBottom: 12 }}>
            <label>{t("products_label_description")}</label>
            <textarea
              className="kb-editor"
              rows={5}
              maxLength={4000}
              placeholder={t("products_placeholder_description")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <div className="kb-char-count">{description.length} / 4000</div>
          </div>

          {/* Variantes */}
          <div className="field" style={{ marginBottom: 12 }}>
            <label>{t("products_label_variants")}</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {variants.map((v, vIndex) => (
                <div
                  key={vIndex}
                  style={{
                    padding: 10,
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    background: "var(--bg-elevated)",
                  }}
                >
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <input
                      type="text"
                      placeholder={t("products_variant_label_placeholder")}
                      value={v.label}
                      onChange={(e) => updateVariantLabel(vIndex, e.target.value)}
                      maxLength={60}
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      className="btn-sm btn-ghost"
                      title={t("products_variant_remove")}
                      onClick={() => removeVariant(vIndex)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {v.options.map((opt, oIndex) => (
                      <div
                        key={oIndex}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          background: "var(--bg-card)",
                          border: "1px solid var(--border)",
                          borderRadius: 6,
                          padding: "2px 4px 2px 8px",
                        }}
                      >
                        <input
                          type="text"
                          placeholder={t("products_variant_option_placeholder")}
                          value={opt}
                          onChange={(e) => updateVariantOption(vIndex, oIndex, e.target.value)}
                          style={{
                            border: "none",
                            outline: "none",
                            background: "transparent",
                            width: 90,
                            fontSize: 13,
                            color: "var(--text)",
                          }}
                        />
                        <button
                          type="button"
                          className="kb-modal-close"
                          onClick={() => removeVariantOption(vIndex, oIndex)}
                          style={{ padding: 2 }}
                          aria-label={t("cancel")}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn-sm btn-ghost"
                      style={{ padding: "4px 8px" }}
                      onClick={() => addVariantOption(vIndex)}
                    >
                      <Plus size={12} /> {t("products_variant_add_option")}
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="btn-sm btn-ghost"
                onClick={addVariant}
                style={{ alignSelf: "flex-start" }}
              >
                <Plus size={14} /> {t("products_variant_add")}
              </button>
            </div>
          </div>

          {/* Actif/Inactif */}
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 12px",
              borderRadius: 8,
              background: "var(--bg-elevated)",
              border: "1px solid var(--border)",
              cursor: "pointer",
              userSelect: "none",
              marginBottom: 12,
            }}
          >
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              style={{ width: 15, height: 15, cursor: "pointer" }}
            />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                {t("products_label_active")}
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>
                {t("products_active_hint")}
              </div>
            </div>
          </label>

          {error && (
            <div style={{ fontSize: 12, color: "var(--red)", padding: "8px 0" }}>{error}</div>
          )}

          <div className="kb-modal-actions">
            <button type="button" className="btn-ghost" onClick={onClose}>
              {t("cancel")}
            </button>
            <button
              type="submit"
              className="btn-primary"
              style={{ width: "auto", padding: "10px 24px" }}
              disabled={saving}
            >
              {saving ? t("saving") : product ? t("products_save") : t("products_create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
